import type { RoiId } from '~/domain/changes/ids';
import { Box, CircularProgress } from '@mui/material';
import { AxisBottom, AxisLeft, AxisTop } from '@visx/axis';
import { GridColumns, GridRows } from '@visx/grid';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CurveData } from '~/domain/patient/dvh';
import {
    isValidNormalizationGy,
    relativeDosePercent,
} from '~/utils/dose/doseDisplayValidation';
import { axisStyle, DARK_CHART_PALETTE } from './chartStyle';
import DvhCurves from './DvhCurves';
import DvhHoverLayer, { hoverCurvesOf } from './DvhHoverLayer';
import { plotAreaWithin } from './plotArea';

type DVHLineChartProps = {
    data: CurveData[];
    normalizationValueGy: number;
    loading?: boolean;
    expanded?: boolean;
    /** The structure drawn at full weight while the others dim, if any. */
    emphasizedRoiId?: RoiId | null;
};

/** Space around the plot for the three axes and their labels, in px. */
const MARGIN = { top: 60, right: 30, bottom: 50, left: 60 };

/** Text sizes by chart size: the expanded chart has room for larger type. */
const FONT_SIZES = {
    compact: { tick: 9, label: 10 },
    expanded: { tick: 11, label: 12 },
};

/** The highest plotted dose, in Gy; 0 with nothing plotted. */
function maxDoseOf(curves: CurveData[]): number {
    let max = 0;
    for (const curve of curves) {
        for (const point of curve.data) {
            max = point.x > max ? point.x : max;
        }
    }
    return max;
}

/**
 * The cumulative DVH chart at a known size: dose in Gy along the bottom,
 * the same doses as a percentage of the normalization dose along the top,
 * and the percentage of each structure's volume up the left. Domain values
 * arrive in Gy and percent and are plotted as they are. A normalization
 * dose that is not a finite dose above 0 Gy has no percentages: the top
 * axis then carries no ticks and says why, and the dose axis spans the
 * plotted doses alone.
 */
export function SizedDvhChart({
    width,
    height,
    data,
    normalizationValueGy,
    expanded = false,
    emphasizedRoiId,
}: DVHLineChartProps & { width: number; height: number }) {
    const intl = useIntl();
    const fonts = expanded ? FONT_SIZES.expanded : FONT_SIZES.compact;
    const plot = plotAreaWithin(width, height, MARGIN);
    const relativeDoseShown = isValidNormalizationGy(normalizationValueGy);
    const maxDoseGy = useMemo(
        () =>
            relativeDoseShown
                ? Math.max(maxDoseOf(data), normalizationValueGy)
                : maxDoseOf(data),
        [data, normalizationValueGy, relativeDoseShown]
    );
    const xScale = useMemo(
        () =>
            scaleLinear<number>({
                domain: [0, maxDoseGy],
                range: [0, plot.width],
            }),
        [maxDoseGy, plot.width]
    );
    const yScale = useMemo(
        () =>
            scaleLinear<number>({
                domain: [0, 100],
                range: [plot.height, 0],
            }),
        [plot.height]
    );
    const hoverCurves = useMemo(() => hoverCurvesOf(data), [data]);
    const palette = DARK_CHART_PALETTE;
    const relativeTick = (doseGy: number) => {
        const percent = relativeDosePercent(doseGy, normalizationValueGy);
        return percent === undefined ? '' : `${percent.toFixed(1)}%`;
    };
    const topAxisStyle = axisStyle(palette, fonts, 'top');

    return (
        <svg width={width} height={height} data-testid="dvh-line-chart">
            <g transform={`translate(${plot.left}, ${plot.top})`}>
                <GridRows
                    scale={yScale}
                    width={plot.width}
                    stroke={palette.grid}
                />
                <GridColumns
                    scale={xScale}
                    height={plot.height}
                    stroke={palette.grid}
                />
                <DvhCurves
                    curves={data}
                    xScale={xScale}
                    yScale={yScale}
                    emphasizedRoiId={emphasizedRoiId}
                />
            </g>
            <AxisBottom
                top={plot.top + plot.height}
                left={plot.left}
                scale={xScale}
                label="Dose (Gy)"
                labelOffset={20}
                {...axisStyle(palette, fonts, 'bottom')}
            />
            <AxisTop
                top={plot.top}
                left={plot.left}
                scale={xScale}
                label={
                    relativeDoseShown
                        ? 'Relative Dose (%)'
                        : intl.formatMessage({ id: 'dvh.relativeDoseNotShown' })
                }
                labelOffset={24}
                tickValues={relativeDoseShown ? undefined : []}
                tickFormat={(value) => relativeTick(Number(value))}
                {...topAxisStyle}
                // The reason is a sentence: it wraps within the plot.
                labelProps={
                    relativeDoseShown
                        ? topAxisStyle.labelProps
                        : { ...topAxisStyle.labelProps, width: plot.width }
                }
            />
            <AxisLeft
                top={plot.top}
                left={plot.left}
                scale={yScale}
                label="Ratio of Total Structure Volume (%)"
                labelOffset={32}
                {...axisStyle(palette, fonts, 'left')}
            />
            <DvhHoverLayer
                curves={data}
                hoverCurves={hoverCurves}
                xScale={xScale}
                yScale={yScale}
                plot={plot}
                normalizationValueGy={normalizationValueGy}
                palette={palette}
            />
        </svg>
    );
}

/**
 * The cumulative DVH chart, filling its container, with a loading overlay
 * while curves are still being computed. It follows a resize on every
 * frame: a re-layout costs well under a frame, and a debounce would show a
 * chart trailing the edge the user drags.
 */
export default function DVHLineChart({
    loading = false,
    ...chart
}: DVHLineChartProps) {
    return (
        <Box position="relative" width="100%" height="100%">
            <ParentSize debounceTime={0}>
                {({ width, height }) =>
                    width > 0 && height > 0 ? (
                        <SizedDvhChart
                            width={width}
                            height={height}
                            {...chart}
                        />
                    ) : null
                }
            </ParentSize>
            {loading && (
                <Box
                    data-testid="dvh-loading-overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        pointerEvents: 'none',
                        background:
                            'radial-gradient(circle at center, rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
                        transition: 'opacity 0.3s',
                    }}
                >
                    <CircularProgress
                        size={64}
                        thickness={4}
                        sx={{ color: '#ffffff', opacity: 0.9 }}
                    />
                </Box>
            )}
        </Box>
    );
}
